import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Col, Row } from 'antd';
import { Information, Title } from 'components/Title/Title';
import { SliderNumber } from 'components/SliderNumber/SliderNumber';
import { cdn } from 'core/cdn';
import { languages } from 'locales/languages';
import styles from "./Tension.module.scss"
import { Buttons } from 'components/Card/Card';
import { Button } from 'components/Button';

const rangeValue = [{
  id: "sys",
  from: 41,
  to: 299,
  default: 120,
},
{
  id: "dia",
  from: 31,
  to: 199,
  default: 80
},
{
  id: "bpm",
  from: 30,
  to: 220,
  default: 60
}
]

const tutorialVideos = {
  arm: "/videos/tutos/tensiometre_bras.mp4",
  wrist: "/videos/tutos/tensiometre_poignet.mp4",
}

export type TensionVideoScreen = keyof typeof tutorialVideos
export type TensionScreen = 'choice' | TensionVideoScreen | 'input'

export const isTensionVideoScreen = (screen: TensionScreen): screen is TensionVideoScreen =>
  screen in tutorialVideos

const Tension = ({ currentValue, setCurrentValue, onSkip, screen, setScreen }: {
  currentValue: any
  setCurrentValue: (value: any) => void
  onSkip: () => void
  screen: TensionScreen
  setScreen: (screen: TensionScreen) => void
}) => {
  const videoRef = useRef(null);
  // a prefilled value means the tension was already recorded: position the
  // sliders on the recorded values
  const prefilled =
    currentValue?.sys !== undefined &&
    currentValue?.dia !== undefined &&
    currentValue?.bpm !== undefined
  const [disabled, setDisabled] = useState<boolean>(!prefilled);
  const [changedValue, setChangedValue] = useState(prefilled ? { dia: true, bpm: true, sys: true } : { dia: false, bpm: false, sys: false });
  const [localValue, setLocalValue] = useState(prefilled ? { ...currentValue } : { sys: 120, dia: 80, bpm: 60 });
  // the sliders remount each time the input screen is reopened (back from the
  // video): start them on the values entered so far, not on the first ones
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialValue = useMemo(() => localValue, [screen]);
  const change = (type: "sys" | "bpm" | "dia", value) => {
    setLocalValue({ ...localValue, ...{ [type]: value } })
    setCurrentValue({ ...localValue, ...{ [type]: value } });
    if (rangeValue.find((r) => r.id === type)?.default !== value) {
      setChangedValue({ ...changedValue, ...{ [type]: true } });
    }
  }

  useEffect(() => {
    if (changedValue.sys && changedValue.bpm && changedValue.dia) {
      setDisabled(false);
    }
  }, [changedValue])


  if (screen === 'choice') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
        <Title text={languages.whichTensiometer} size="medium" doNotAnimateTitle={true} />
        <div className={styles.Choices}>
          <div className={styles.Choice} onClick={() => setScreen('arm')}>
            <img src={cdn("/images/vitals/tensiometre_bras.png")} alt="" />
            <span>{languages.armTensiometer}</span>
          </div>
          <div className={styles.Choice} onClick={() => setScreen('wrist')}>
            <img src={cdn("/images/vitals/tensiometre_poignet.png")} alt="" />
            <span>{languages.wristTensiometer}</span>
          </div>
        </div>
      </div>
    )
  }
  if (isTensionVideoScreen(screen)) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%' }}>

        <Title text={languages.takeYourTension} doNotAnimateTitle={true} />
        <video key={screen} style={{ borderRadius: "15px", width: '-webkit-fill-available', maxHeight: "500px", marginTop: '20px', marginBottom: '40px' }} width={960} height={480} autoPlay id="remote-media"
          muted
          ref={videoRef}
          onEnded={() => {
            videoRef?.current?.play();
          }}>
          <source src={cdn(tutorialVideos[screen])} type="video/mp4" />
          {languages.unableToProcessVideo}
        </video>
        <Buttons>
          <Button wide='long' htmlType='button' onClick={onSkip}>{languages.skipStep}</Button>
          <Button wide="long" htmlType='button' type='primary' onClick={() => setScreen('input')}>{languages.iTookMyTension}</Button>
        </Buttons>
      </div>
    )
  }
  if (screen === 'input') {
    return (
      // without its own key, React reuses the video's « J'ai pris » button as
      // the submit button: once Confirmer is enabled, that same click submits
      <div key="input">
        <Title text={languages.addYourTension} doNotAnimateTitle={true} />
        <Row>
          <Col span={1} style={{ paddingTop: '20px' }}>
            <div style={{ justifyContent: "space-around", display: "flex", flexDirection: "column", height: "100%" }}>
              <div>
                {changedValue.sys &&
                  <img src={cdn('/images/vitals/tension_success.svg')} alt="success_blue_icon" />
                }
              </div>
              <div>
                {changedValue.dia &&
                  <img src={cdn('/images/vitals/tension_success.svg')} alt="success_blue_icon" />
                }
              </div>
              <div>
                {changedValue.bpm &&
                  <img src={cdn('/images/vitals/tension_success.svg')} alt="success_blue_icon" />
                }
              </div>
            </div>
          </Col>
          <Col span={3} style={{ paddingTop: '40px' }}>
            <div style={{ justifyContent: "space-around", display: "flex", flexDirection: "column", height: "100%" }}>
              <div>
                <Information size="large" text="SYS" style={{ marginBottom: 0, marginRight: 0 }} />
                <Information text="mmHg" />
              </div>
              <div>
                <Information size="large" text="DIA" style={{ marginBottom: 0 }} />
                <Information text="mmHg" />
              </div>
              <div>
                <Information size="large" text="PULSE" style={{ marginBottom: 0 }} />
                <Information text="/min" />
              </div>
            </div>
          </Col>

          <Col span={18}>
            <div className={styles.Container}>
              {rangeValue.map((range, index) =>
                <SliderNumber
                  key={`slider-${index}`}
                  minValue={range.from}
                  maxValue={range.to}
                  defaultValue={initialValue[range.id] ?? range.default}
                  onChange={(val) => {
                    change(range.id as "sys" | "bpm" | "dia", val)
                  }} />
              )}
            </div>
          </Col>
        </Row>
        <Buttons>
          <Button wide='long' type="link" htmlType='button' onClick={onSkip}>{languages.skipStep}</Button>
          <Button wide='long' type="primary" htmlType='submit' disabled={disabled}>{languages.confirm}</Button>
        </Buttons>
      </div>
    )
  }
}

export default Tension